import React from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import DateOption from "../grossesse/DateOption";
import InputDate from "../grossesse/InputDate";
import DateLine from "../grossesse/DateLine";
import { isPlausible, lastPeriodFrom, pregnancyAt } from "./saRules";

const FIELD_COPY = {
    lastPeriod: {
        label: "Date de vos dernières règles",
        hint: "Le 1er jour de vos dernières règles : c'est la base des semaines d'aménorrhée.",
    },
    startPregnancy: {
        label: "Date de début de grossesse",
        hint: "La date de conception, si vous la connaissez.",
    },
    endPregnancy: {
        label: "Date prévue d'accouchement",
        hint: "Le terme estimé par votre médecin ou votre échographie de datation.",
    },
};

const long = (date) => format(date, "EEEE d MMMM yyyy", { locale: fr }).replace(/^(\S+) 1 /, "$1 1er ");
const ordinal = (n) => (n === 1 ? "1er" : `${n}e`);
const plural = (n, word) => `${n} ${word}${n > 1 ? "s" : ""}`;
const todayIso = () => format(new Date(), "yyyy-MM-dd");
// Local midnight: new Date("yyyy-mm-dd") is UTC and can land on the previous day.
const parse = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);

function StatCard({ color, title, value }) {
    return (
        <div className={`w-1/2 ${color} rounded-lg p-4 shadow-md flex flex-col justify-between`}>
            <div className="text-xs text-white sm:text-base w-full cursor-default">{title}</div>
            <div className="text-xl w-full text-center font-bold text-white mt-2">{value}</div>
        </div>
    );
}

export default function SemaineGrossesse() {
    const [dateOption, setDateOption] = React.useState("lastPeriod");
    const [inputDate, setInputDate] = React.useState("");
    const [refDate, setRefDate] = React.useState(todayIso());

    const copy = FIELD_COPY[dateOption];
    const base = parse(inputDate);
    const ref = parse(refDate);
    const p = isPlausible(base) && isPlausible(ref) ? pregnancyAt(lastPeriodFrom(dateOption, base), ref) : null;
    const isToday = refDate === todayIso();

    return (
        <>
            <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg flex flex-col gap-3">
                <DateOption dateOption={dateOption} handleDateOptionChange={(e) => setDateOption(e.target.value)} />
                <InputDate inputDate={inputDate} handleInputDateChange={(e) => setInputDate(e.target.value)} label={copy.label} hint={copy.hint} />
                <div>
                    <label htmlFor="refDate" className="block text-sm sm:text-base mb-2 font-semibold text-gray-900">Calculer à la date du</label>
                    <input
                        type="date"
                        id="refDate"
                        value={refDate}
                        onChange={(e) => setRefDate(e.target.value || todayIso())}
                        min="2000-01-01"
                        max="2035-12-31"
                        className="dropdown min-w-32 sm:max-w-56"
                    />
                    <p className="text-xs text-gray-600 mt-2">Aujourd'hui par défaut. Changez-la pour savoir à combien de semaines vous serez à une date donnée.</p>
                </div>
            </div>

            {!p && (
                <div className="p-6 my-2 sm:my-4 bg-stone-50 border border-dashed border-stone-300 rounded-lg text-center">
                    <p className="text-gray-900 font-semibold text-sm sm:text-base">
                        Renseignez une date pour calculer votre semaine de grossesse
                    </p>
                    <p className="text-gray-600 text-sm mt-2">
                        Vous obtiendrez vos semaines d'aménorrhée (SA), vos semaines de grossesse (SG), votre mois, votre trimestre et votre date prévue d'accouchement.
                    </p>
                </div>
            )}

            {p && p.status === "notStarted" && (
                <div className="p-6 my-2 sm:my-4 bg-stone-50 border border-dashed border-stone-300 rounded-lg text-center">
                    <p className="text-gray-900 font-semibold text-sm sm:text-base">
                        Cette date est antérieure au début de la grossesse
                    </p>
                    <p className="text-gray-600 text-sm mt-2">
                        Les semaines d'aménorrhée se comptent à partir du {long(p.lastPeriod)}.
                    </p>
                </div>
            )}

            {p && p.status !== "notStarted" && (
                <>
                    <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">
                        Résultats {isToday ? "aujourd'hui" : `au ${format(ref, "dd/MM/yyyy")}`}
                    </div>

                    <div className="p-4 my-2 sm:my-4 bg-lime-100 rounded-lg text-center">
                        <p className="text-sm text-gray-700">{isToday ? "Vous êtes à" : refDate < todayIso() ? "Vous étiez à" : "Vous serez à"}</p>
                        <p className="text-2xl sm:text-3xl font-bold text-gray-900 my-1">
                            {p.sa} SA{p.saDays > 0 && ` + ${plural(p.saDays, "jour")}`}
                        </p>
                        <p className="text-sm text-gray-700">
                            {p.sg === null
                                ? "La conception n'a pas encore eu lieu (moins de 2 SA)."
                                : <>soit <strong>{p.sg} SG{p.sgDays > 0 && ` + ${plural(p.sgDays, "jour")}`}</strong> (semaines de grossesse)</>}
                        </p>
                        <div className="w-full bg-white rounded-full h-2.5 mt-4" role="progressbar" aria-valuenow={p.progress} aria-valuemin="0" aria-valuemax="100" aria-label="Avancement de la grossesse">
                            <div className="bg-lime-600 h-2.5 rounded-full" style={{ width: `${p.progress}%` }}></div>
                        </div>
                        <p className="text-xs text-gray-700 mt-1">{p.progress} % de la grossesse (terme à 41 SA)</p>
                    </div>

                    <div className="flex items-stretch gap-2 sm:gap-4">
                        <StatCard color="bg-sky-700" title="Mois de grossesse" value={`${ordinal(p.month)} mois`} />
                        <StatCard color="bg-indigo-900" title="Trimestre" value={`${ordinal(p.trimester)} trimestre`} />
                    </div>

                    <div className="p-4 my-2 sm:my-4 bg-teal-700 rounded-lg">
                        <DateLine description="Dernières règles" date={long(p.lastPeriod)} />
                        <DateLine description="Conception estimée" date={long(p.conception)} />
                        <DateLine description="Date prévue d'accouchement" date={long(p.dueDate)} />
                        <DateLine
                            description={p.status === "overdue" ? "Terme dépassé de" : "Avant le terme"}
                            date={plural(Math.abs(p.daysToTerm), "jour")}
                        />
                        {p.status === "fullTerm" && (
                            <p className="text-white text-xs mt-2">Votre grossesse est à terme depuis 37 SA.</p>
                        )}
                        {p.status === "overdue" && (
                            <p className="text-white text-xs mt-2">Au-delà du terme, un suivi rapproché est organisé par la maternité : suivez les consignes de votre équipe médicale.</p>
                        )}
                    </div>

                    <p className="text-xs text-gray-600 text-center">
                        Pour le calendrier complet (échographies, consultations, congé maternité), utilisez la{" "}
                        <a href="/outils/roulette-obstetricale" className="underline font-semibold text-gray-900">roulette obstétricale</a>.
                    </p>
                </>
            )}
        </>
    );
}
